import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Colxx } from "../../../components/common/CustomBootstrap";
import HotTable from "@handsontable/react";
import {
  Row,
  Col,
  Badge,
  Input,
  Label,
  Card,
  CardBody,
  Button,
  Alert,
} from "reactstrap";
import {
  NotificatioErrController,
  NotificatioSucController,
} from "../../../helpers/Utils";
import { HandlerPostRequest } from "../../../helpers/Axios";
import { injectIntl } from "react-intl";

// DB fields mà user có thể map
const DB_FIELDS = [
  { value: "name", labelKey: "product.ocr.col-name", fallback: "Tên sản phẩm", required: true },
  { value: "category", labelKey: "product.ocr.col-category", fallback: "Danh mục" },
  { value: "price", labelKey: "product.ocr.col-price", fallback: "Giá bán", type: "number" },
  { value: "price_sale", labelKey: "product.ocr.col-price-sale", fallback: "Giá khuyến mãi", type: "number" },
  { value: "barcode", labelKey: "product.ocr.col-barcode", fallback: "Mã vạch" },
  { value: "size", labelKey: "product.ocr.col-size", fallback: "Kích thước" },
  { value: "brand_name", labelKey: "product.brand-name", fallback: "Thương hiệu" },
  { value: "sale_unit", labelKey: "product.sale-unit", fallback: "Đơn vị bán" },
  { value: "fnb_type", labelKey: "product.fnb-type", fallback: "Loại món (food/drink)" },
  { value: "price_wholesale", labelKey: "product.price-wholesale", fallback: "Giá bán sỉ", type: "number" },
  { value: "stock_quantity", labelKey: "product.stock-quantity", fallback: "Tồn ban đầu", type: "number" },
  { value: "stock_cost_price", labelKey: "product.stock-cost-price", fallback: "Giá vốn", type: "number" },
];

const STEPS = { UPLOAD: 1, MAPPING: 2, PREVIEW: 3 };

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(",")[1];
      resolve({ base64, mimeType: file.type });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Step indicator component
function StepIndicator({ currentStep, messages }) {
  const steps = [
    { step: STEPS.UPLOAD, labelKey: "product.ocr.step-upload", fallback: "Tải ảnh lên" },
    { step: STEPS.MAPPING, labelKey: "product.ocr.step-mapping", fallback: "Ghép cột" },
    { step: STEPS.PREVIEW, labelKey: "product.ocr.step-preview", fallback: "Xem & Lưu" },
  ];
  return (
    <div className="d-flex justify-content-center mb-4">
      {steps.map((s, i) => (
        <React.Fragment key={s.step}>
          <div className="text-center" style={{ minWidth: 80 }}>
            <Badge
              color={currentStep >= s.step ? "primary" : "secondary"}
              pill
              style={{ width: 30, height: 30, lineHeight: "22px", fontSize: 14 }}
            >
              {s.step}
            </Badge>
            <div className="mt-1 small" style={{ color: currentStep >= s.step ? "#145388" : "#999" }}>
              {messages[s.labelKey] || s.fallback}
            </div>
          </div>
          {i < steps.length - 1 && (
            <div
              style={{
                flex: 1,
                height: 2,
                background: currentStep > s.step ? "#145388" : "#ddd",
                alignSelf: "center",
                marginTop: -12,
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// Column mapping row
function MappingRow({ aiColumn, dbField, sampleValues, messages, usedFields, onChange }) {
  const availableFields = DB_FIELDS.filter(
    (f) => f.value === dbField || !usedFields.includes(f.value)
  );
  return (
    <Row className="align-items-center mb-2 py-1" style={{ borderBottom: "1px solid #eee" }}>
      <Col sm="4">
        <span className="font-weight-bold">{aiColumn}</span>
        <div className="text-muted small text-truncate" title={sampleValues}>
          {sampleValues}
        </div>
      </Col>
      <Col sm="1" className="text-center">
        <i className="simple-icon-arrow-right" />
      </Col>
      <Col sm="4">
        <Input
          type="select"
          bsSize="sm"
          value={dbField || ""}
          onChange={(e) => onChange(aiColumn, e.target.value || null)}
        >
          <option value="">{messages["product.ocr.skip-column"] || "-- Bỏ qua --"}</option>
          {availableFields.map((f) => (
            <option key={f.value} value={f.value}>
              {messages[f.labelKey] || f.fallback}
              {f.required ? " *" : ""}
            </option>
          ))}
        </Input>
      </Col>
      <Col sm="3">
        {dbField && (
          <Badge color="success" pill className="px-2">
            {messages["product.ocr.mapped"] || "Đã ghép"}
          </Badge>
        )}
      </Col>
    </Row>
  );
}

function AddProductsFromImage({ intl, onSaveSuccess, businessType }) {
  const { messages } = intl;

  // State
  const [step, setStep] = useState(STEPS.UPLOAD);
  const [images, setImages] = useState([]); // [{file, preview}]
  const [customPrompt, setCustomPrompt] = useState("");
  const [showPrompt, setShowPrompt] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  // OCR result
  const [ocrColumns, setOcrColumns] = useState([]); // ["name", "category", ...]
  const [ocrData, setOcrData] = useState([]); // raw rows from AI
  const [columnMapping, setColumnMapping] = useState({}); // { aiCol: dbField }
  const [rawResult, setRawResult] = useState("");

  // Save result
  const [saveSummary, setSaveSummary] = useState(null);

  const objectUrlsRef = useRef([]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // Danh sách DB fields đã được map
  const usedFields = useMemo(
    () => Object.values(columnMapping).filter(Boolean),
    [columnMapping]
  );

  // Preview data (mapped)
  const mappedPreviewData = useMemo(() => {
    if (ocrData.length === 0) return [];
    return ocrData.map((row) => {
      const mapped = {};
      for (const [aiCol, dbField] of Object.entries(columnMapping)) {
        if (dbField && row[aiCol] !== undefined) {
          mapped[dbField] = row[aiCol];
        }
      }
      return mapped;
    });
  }, [ocrData, columnMapping]);

  // Validation: name phải được map
  const isNameMapped = usedFields.includes("name");

  // Sample values for mapping display
  const getSampleValues = useCallback(
    (col) => {
      const samples = ocrData
        .slice(0, 3)
        .map((r) => r[col])
        .filter((v) => v !== undefined && v !== "");
      return samples.join(", ");
    },
    [ocrData]
  );

  // ========== Step 1: Upload ==========
  const handleImageAdd = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newImages = files.map((file) => {
      const url = URL.createObjectURL(file);
      objectUrlsRef.current.push(url);
      return { file, preview: url };
    });

    setImages((prev) => [...prev, ...newImages]);
    setError("");
    // Reset input
    e.target.value = "";
  }, []);

  const handleImageRemove = useCallback((index) => {
    setImages((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleExtract = useCallback(async () => {
    if (images.length === 0) {
      setError(messages["product.ocr.error-no-image"] || "Vui lòng chọn ít nhất một hình ảnh.");
      return;
    }

    setLoading(true);
    setError("");
    setOcrColumns([]);
    setOcrData([]);
    setRawResult("");

    try {
      let allColumns = new Set();
      let allRows = [];

      // Xử lý từng ảnh
      for (const img of images) {
        const { base64, mimeType } = await fileToBase64(img.file);

        const response = await HandlerPostRequest({
          path: "/product/ocr-from-image",
          body: {
            prompt: customPrompt || undefined,
            imageBase64: base64,
            mimeType,
            businessType: businessType || "",
          },
        });

        if (response.columns) {
          response.columns.forEach((c) => allColumns.add(c));
        }
        if (response.data && response.data.length > 0) {
          allRows = allRows.concat(response.data);
        }
        if (response.rawResult) {
          setRawResult(response.rawResult);
        }

        // Áp dụng suggested mapping từ lần đầu
        if (response.suggestedMapping && Object.keys(columnMapping).length === 0) {
          setColumnMapping(response.suggestedMapping);
        }
      }

      if (allRows.length === 0) {
        setError(
          messages["product.ocr.error-no-data"] ||
            "Không thể trích xuất dữ liệu từ hình ảnh."
        );
        return;
      }

      const cols = Array.from(allColumns);
      setOcrColumns(cols);
      setOcrData(allRows);
      setStep(STEPS.MAPPING);
    } catch (err) {
      const errorMessage =
        err.response?.data?.responseText ||
        messages["product.ocr.error-extract"] ||
        "Đã xảy ra lỗi khi phân tích hình ảnh.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [images, customPrompt, businessType, messages, columnMapping]);

  // ========== Step 2: Mapping ==========
  const handleMappingChange = useCallback((aiCol, dbField) => {
    setColumnMapping((prev) => ({ ...prev, [aiCol]: dbField }));
  }, []);

  // ========== Step 3: Save ==========
  const handleSaveAll = useCallback(async () => {
    if (!isNameMapped) {
      setError(messages["product.ocr.error-name-required"] || "Phải ghép cột 'Tên sản phẩm' trước khi lưu.");
      return;
    }

    setIsSaving(true);
    setError("");
    setSaveSummary(null);

    try {
      const response = await HandlerPostRequest({
        path: "/product/bulk-upsert-from-image",
        body: {
          data: ocrData,
          mapping: columnMapping,
        },
      });

      NotificatioSucController(
        response.responseText ||
          messages["product.ocr.save-success"] ||
          "Import sản phẩm thành công!"
      );

      if (response.summary) {
        setSaveSummary(response.summary);
      }

      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.responseText ||
        messages["product.ocr.error-save"] ||
        "Có lỗi xảy ra, vui lòng thử lại.";
      NotificatioErrController(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [ocrData, columnMapping, isNameMapped, messages, onSaveSuccess]);

  // Reset all
  const handleReset = useCallback(() => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current = [];
    setStep(STEPS.UPLOAD);
    setImages([]);
    setOcrColumns([]);
    setOcrData([]);
    setColumnMapping({});
    setRawResult("");
    setError("");
    setSaveSummary(null);
    setCustomPrompt("");
  }, []);

  // HotTable columns cho preview
  const previewColumns = useMemo(() => {
    const mapped = DB_FIELDS.filter((f) => usedFields.includes(f.value));
    return mapped.map((f) => ({
      data: f.value,
      type: f.type === "number" ? "numeric" : "text",
      ...(f.type === "number" ? { numericFormat: { pattern: "0,0" } } : {}),
    }));
  }, [usedFields]);

  const previewHeaders = useMemo(() => {
    return DB_FIELDS.filter((f) => usedFields.includes(f.value)).map(
      (f) => messages[f.labelKey] || f.fallback
    );
  }, [usedFields, messages]);

  // Stats for preview
  const previewStats = useMemo(() => {
    if (mappedPreviewData.length === 0) return null;
    const withPrice = mappedPreviewData.filter((r) => r.price > 0).length;
    const categories = new Set(mappedPreviewData.map((r) => r.category).filter(Boolean));
    return {
      total: mappedPreviewData.length,
      withPrice,
      categories: categories.size,
    };
  }, [mappedPreviewData]);

  return (
    <div>
      <StepIndicator currentStep={step} messages={messages} />

      {error && (
        <Alert color="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {/* ============ STEP 1: UPLOAD ============ */}
      {step === STEPS.UPLOAD && (
        <Row>
          <Colxx xxs="6" className="mb-4">
            <h5>{messages["product.ocr.title"] || "Trích xuất sản phẩm từ hình ảnh"}</h5>
            <p className="text-muted small">
              {messages["product.ocr.description"] || "Upload ảnh menu/bảng giá, AI sẽ tự động nhận diện sản phẩm."}
            </p>

            <Label className="btn btn-outline-primary mb-2" style={{ cursor: "pointer" }}>
              <i className="simple-icon-cloud-upload mr-1" />
              {messages["product.ocr.choose-images"] || "Chọn hình ảnh"}
              <input
                type="file"
                onChange={handleImageAdd}
                accept="image/*"
                multiple
                style={{ display: "none" }}
              />
            </Label>

            <div className="mb-3">
              {images.map((img, i) => (
                <div
                  key={i}
                  className="d-inline-block position-relative mr-2 mb-2"
                  style={{ width: 100, height: 100 }}
                >
                  <img
                    src={img.preview}
                    alt={`img-${i}`}
                    style={{
                      width: 100,
                      height: 100,
                      objectFit: "cover",
                      borderRadius: 6,
                      border: "1px solid #ddd",
                    }}
                  />
                  <Badge
                    color="danger"
                    pill
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      cursor: "pointer",
                      fontSize: 10,
                    }}
                    onClick={() => handleImageRemove(i)}
                  >
                    x
                  </Badge>
                </div>
              ))}
            </div>

            {/* Prompt tùy chỉnh (ẩn/hiện) */}
            <div className="mb-3">
              <a
                href="#!"
                className="small text-muted"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPrompt(!showPrompt);
                }}
              >
                {showPrompt
                  ? messages["product.ocr.hide-prompt"] || "Ẩn prompt tùy chỉnh"
                  : messages["product.ocr.show-prompt"] || "Tùy chỉnh prompt AI"}
              </a>
              {showPrompt && (
                <textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder={messages["product.ocr.prompt-placeholder"] || "Để trống = tự động theo loại hình kinh doanh"}
                  rows="4"
                  className="form-control mt-2"
                />
              )}
            </div>

            <Button
              color="primary"
              onClick={handleExtract}
              disabled={loading || images.length === 0}
            >
              {loading
                ? messages["product.ocr.extracting"] || "Đang xử lý..."
                : messages["product.ocr.extract-btn"] || "Trích xuất dữ liệu"}
            </Button>
          </Colxx>

          <Colxx xxs="6" className="mb-4">
            {images.length > 0 && (
              <Card>
                <CardBody className="text-center">
                  <img
                    src={images[0].preview}
                    alt="Main preview"
                    style={{ maxWidth: "100%", maxHeight: 400, borderRadius: 8 }}
                  />
                  {images.length > 1 && (
                    <p className="text-muted small mt-2">
                      +{images.length - 1} {messages["product.ocr.more-images"] || "ảnh khác"}
                    </p>
                  )}
                </CardBody>
              </Card>
            )}
          </Colxx>
        </Row>
      )}

      {/* ============ STEP 2: COLUMN MAPPING ============ */}
      {step === STEPS.MAPPING && (
        <div>
          <Row className="mb-3">
            <Col>
              <h5>
                {messages["product.ocr.mapping-title"] || "Ghép cột dữ liệu"}
              </h5>
              <p className="text-muted small">
                {messages["product.ocr.mapping-desc"] ||
                  "AI đã nhận diện các cột bên trái. Hãy ghép chúng với trường dữ liệu sản phẩm tương ứng bên phải."}
              </p>
            </Col>
          </Row>

          <Card className="mb-3">
            <CardBody>
              {/* Header */}
              <Row className="mb-2 font-weight-bold small text-uppercase text-muted">
                <Col sm="4">{messages["product.ocr.ai-column"] || "Cột từ AI"}</Col>
                <Col sm="1" />
                <Col sm="4">{messages["product.ocr.db-field"] || "Trường dữ liệu"}</Col>
                <Col sm="3">{messages["product.ocr.status"] || "Trạng thái"}</Col>
              </Row>

              {ocrColumns.map((col) => (
                <MappingRow
                  key={col}
                  aiColumn={col}
                  dbField={columnMapping[col] || null}
                  sampleValues={getSampleValues(col)}
                  messages={messages}
                  usedFields={usedFields}
                  onChange={handleMappingChange}
                />
              ))}
            </CardBody>
          </Card>

          {!isNameMapped && (
            <Alert color="warning">
              {messages["product.ocr.error-name-required"] || "Bắt buộc ghép cột 'Tên sản phẩm' để tiếp tục."}
            </Alert>
          )}

          <div className="d-flex justify-content-between">
            <Button color="secondary" outline onClick={() => setStep(STEPS.UPLOAD)}>
              {messages["product.ocr.back"] || "Quay lại"}
            </Button>
            <div>
              <span className="text-muted small mr-3">
                {ocrData.length} {messages["product.ocr.rows-found"] || "dòng dữ liệu"} |{" "}
                {usedFields.length}/{DB_FIELDS.length}{" "}
                {messages["product.ocr.cols-mapped"] || "cột đã ghép"}
              </span>
              <Button
                color="primary"
                onClick={() => setStep(STEPS.PREVIEW)}
                disabled={!isNameMapped}
              >
                {messages["product.ocr.next"] || "Tiếp tục"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ============ STEP 3: PREVIEW & SAVE ============ */}
      {step === STEPS.PREVIEW && (
        <div>
          <Row className="mb-3">
            <Col>
              <h5>{messages["product.ocr.preview-title"] || "Xem trước & Lưu"}</h5>
            </Col>
          </Row>

          {/* Stats */}
          {previewStats && (
            <Row className="mb-3">
              <Col sm="4">
                <Card>
                  <CardBody className="text-center py-2">
                    <h4 className="mb-0">{previewStats.total}</h4>
                    <small className="text-muted">
                      {messages["product.ocr.stat-products"] || "Sản phẩm"}
                    </small>
                  </CardBody>
                </Card>
              </Col>
              <Col sm="4">
                <Card>
                  <CardBody className="text-center py-2">
                    <h4 className="mb-0">{previewStats.categories}</h4>
                    <small className="text-muted">
                      {messages["product.ocr.stat-categories"] || "Danh mục"}
                    </small>
                  </CardBody>
                </Card>
              </Col>
              <Col sm="4">
                <Card>
                  <CardBody className="text-center py-2">
                    <h4 className="mb-0">{previewStats.withPrice}</h4>
                    <small className="text-muted">
                      {messages["product.ocr.stat-with-price"] || "Có giá"}
                    </small>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Data table */}
          {mappedPreviewData.length > 0 && previewColumns.length > 0 && (
            <HotTable
              data={mappedPreviewData}
              colHeaders={previewHeaders}
              columns={previewColumns}
              rowHeaders={true}
              width="100%"
              height={Math.min(400, mappedPreviewData.length * 30 + 50)}
              stretchH="all"
              licenseKey="non-commercial-and-evaluation"
              contextMenu={true}
            />
          )}

          {/* Save summary after import */}
          {saveSummary && (
            <Alert color="success" className="mt-3">
              <strong>{messages["product.ocr.import-done"] || "Hoàn tất!"}</strong>{" "}
              {saveSummary.created > 0 &&
                `${messages["product.ocr.created"] || "Tạo mới"}: ${saveSummary.created}. `}
              {saveSummary.updated > 0 &&
                `${messages["product.ocr.updated"] || "Cập nhật"}: ${saveSummary.updated}. `}
              {saveSummary.skipped > 0 &&
                `${messages["product.ocr.skipped"] || "Bỏ qua"}: ${saveSummary.skipped}.`}
            </Alert>
          )}

          <div className="d-flex justify-content-between mt-3">
            <div>
              <Button color="secondary" outline onClick={() => setStep(STEPS.MAPPING)} className="mr-2">
                {messages["product.ocr.back"] || "Quay lại"}
              </Button>
              <Button color="light" onClick={handleReset}>
                {messages["product.ocr.reset"] || "Làm mới"}
              </Button>
            </div>
            <Button
              color="primary"
              onClick={handleSaveAll}
              disabled={isSaving || mappedPreviewData.length === 0}
            >
              {isSaving
                ? messages["product.ocr.saving"] || "Đang lưu..."
                : `${messages["product.ocr.save-btn"] || "Lưu"} (${mappedPreviewData.length} ${
                    messages["product.ocr.items"] || "sản phẩm"
                  })`}
            </Button>
          </div>
        </div>
      )}

      {/* Raw result debug (hiển thị khi parse thất bại) */}
      {rawResult && ocrData.length === 0 && (
        <div className="mt-3">
          <h6>{messages["product.ocr.raw-result"] || "Kết quả thô từ AI:"}</h6>
          <pre className="bg-light p-2 rounded small" style={{ maxHeight: 200, overflow: "auto" }}>
            {rawResult}
          </pre>
        </div>
      )}
    </div>
  );
}

export default injectIntl(AddProductsFromImage);
